import { MouseEvent, KeyboardEvent, useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { MemriseApiError, PathwaysApi, TrackingApi } from 'src/api';
import { PronunciationUpsellTrackingAction } from 'src/api/tracking/types';
import { useUserContext } from 'src/context/user';
import { usePremiumConfig } from 'src/hooks/usePremiumConfig';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { LocalPronunciationClip, useLocalPronunciationClip } from './useLocalPronunciationClip';

/** Where the screen was triggered from — also the `source` passed on to the plans page. */
export type PronunciationUpsellSource =
  'pronunciation_first_session' | 'pronunciation_no_tokens' | 'say_it_yourself_no_tokens';

/**
 * Which moment stopped the learner: `session_end` / `start_lesson` in the pronunciation
 * session, `last_attempt` / `prompt_tap` in the Learn session's "Say it yourself" practice.
 */
export type PronunciationUpsellTrigger =
  'session_end' | 'start_lesson' | 'last_attempt' | 'prompt_tap';

export type PronunciationUpsellDismissMethod = 'stay_on_free' | 'outside_click' | 'escape';

/** Tells a modal close apart: react-modal requests one on Esc (keydown) or an overlay click. */
export const getModalDismissMethod = (
  event?: MouseEvent | KeyboardEvent,
): PronunciationUpsellDismissMethod => (event?.type === 'keydown' ? 'escape' : 'outside_click');

export const usePronunciationUpsell = ({
  source,
  onContinue,
  clipOverride,
  trigger,
}: {
  source: PronunciationUpsellSource;
  onContinue: () => void;
  clipOverride?: LocalPronunciationClip;
  trigger?: PronunciationUpsellTrigger;
}) => {
  const intl = useIntl();
  const { user } = useUserContext();
  const languagePairId = getLanguagePairId();
  const { premiumConfig } = usePremiumConfig({});
  const learntWordClip = useLocalPronunciationClip();
  const clip = clipOverride ?? learntWordClip;
  const yearlyPrice = premiumConfig?.prices.find(price => price.paymentPeriod === 'year');

  /* Shares the Path screen's cache key, so a learner arriving from the path is a cache hit. */
  const { data: activePathway, isPending: isPathwayPending } = useQuery({
    queryKey: ['activePathway', languagePairId],
    enabled: !!languagePairId,
    retry: false,
    queryFn: async () => {
      try {
        return await PathwaysApi.getActivePathway(languagePairId as number);
      } catch (error) {
        if (error instanceof MemriseApiError && error.httpStatusCode === 404) return null;
        throw error;
      }
    },
  });
  const skillLevelId = activePathway?.skillLevel.id;
  const hasSettledSkillLevel = !languagePairId || !isPathwayPending;

  const trackAction = useCallback(
    (action: PronunciationUpsellTrackingAction, extraData: Record<string, string> = {}) => {
      void TrackingApi.trackUserAction(
        action,
        'pronunciation_upsell',
        languagePairId,
        !!user?.isPro,
        {
          source,
          ...(trigger && { trigger }),
          ...(skillLevelId != null && { skill_level_id: String(skillLevelId) }),
          ...extraData,
        },
      );
    },
    [languagePairId, user?.isPro, source, trigger, skillLevelId],
  );

  const hasTrackedView = useRef(false);
  useEffect(() => {
    /*
     * Held until the pathway lookup settles so the view carries `skill_level_id` like the
     * outcome events do. Deliberately without `has_clip`: the footage is still being looked
     * up at this point, so any value would report "no clip" for learners who went on to see
     * one. The outcome events below carry it once the lookup has settled.
     */
    if (hasTrackedView.current || !hasSettledSkillLevel) return;
    hasTrackedView.current = true;
    trackAction('screen_viewed');
  }, [hasSettledSkillLevel, trackAction]);

  /** Whether the learner was actually shown a local saying a word they know. */
  const hasClip = String(!!clip);

  const handleSubscribeClick = useCallback(() => {
    trackAction('clicked_subscribe', { has_clip: hasClip });
  }, [trackAction, hasClip]);

  const handleDismiss = useCallback(
    (dismissMethod: PronunciationUpsellDismissMethod) => {
      trackAction('paywall_dismissed', { has_clip: hasClip, dismiss_method: dismissMethod });
      onContinue();
    },
    [trackAction, onContinue, hasClip],
  );

  const formattedYearlyPrice =
    yearlyPrice &&
    intl.formatNumber(yearlyPrice.finalPrice, {
      style: 'currency',
      currency: yearlyPrice.currency,
      // A round price reads better bare; 57.59 still keeps its pence.
      minimumFractionDigits: Number.isInteger(yearlyPrice.finalPrice) ? 0 : 2,
    });

  return { clip, formattedYearlyPrice, handleSubscribeClick, handleDismiss };
};
